import {
  createAlignPlugin,
  createBoldPlugin,
  createItalicPlugin,
  createParagraphPlugin,
  createLinkPlugin,
  createExitBreakPlugin,
  createUnderlinePlugin,
  createNodeIdPlugin,
  createResetNodePlugin,
  createTrailingBlockPlugin,
  createListPlugin,
  ELEMENT_H1,
  ELEMENT_H2,
  ELEMENT_H3,
  ELEMENT_H4,
  ELEMENT_H5,
  ELEMENT_H6,
  ELEMENT_PARAGRAPH,
  ELEMENT_LINK,
  ELEMENT_UL,
  ELEMENT_OL,
  ELEMENT_LI,
  ELEMENT_TODO_LI,
  ELEMENT_MEDIA_EMBED,
  ELEMENT_TABLE,
  ELEMENT_TD,
  ELEMENT_TH,
  ELEMENT_IMAGE,
  MARK_BOLD,
  MARK_UNDERLINE,
  MARK_ITALIC,
} from '@udecode/plate';
import { createPlugins } from '@udecode/plate-core';
import { PlateLeaf } from '@udecode/plate-common';

import { withPlaceholders } from '../components/placeholder/placeholder';
import { createErrorHandlingPlugin } from '../plugins/error/createErrorHandlingPlugin';
import { listPlugin, listPluginByKey } from '../plugins/list/listPlugin';
import { linkPlugin } from '../plugins/link/linkPlugin';
import { LinkFloatingToolbar } from '../plugins/link/LinkFloatingToolbar';
import { VALID_ALIGNMENT_TYPES } from '../plugins/alignment/constants';
import { createFakeSelectionPlugin } from '../plugins/fake-selection/createFakeSelectionPlugin';
import { resetNodePlugin } from '../plugins/reset-node/resetNodePlugin';
import {
  createMergeFieldsPlugin,
  ELEMENT_MERGE_FIELD,
  ELEMENT_SMART_INPUT,
} from '../plugins/merge-fields/createMergeFieldsPlugin';
import { HeadingElement } from '../plugins/heading/HeadingElement';
import { ParagraphElement } from '../plugins/paragraph/ParagraphElement';
import { ListElement } from '../plugins/list/ListElement';
import { TableElement } from '../plugins/table/TableElement';
import { TableCellElement } from '../plugins/table/TableCellElement';
import { TodoListElement } from '../plugins/todo-list/ToDoListElement';
import { ELEMENT_PAGE_BREAK } from '../plugins/page-break/createPageBreakPlugin';
import { PageBreakElement } from '../plugins/page-break/PageBreakElement';
import { ImageElement2 } from '../plugins/media/images/ImageElement2';
import { MediaEmbedElement2 } from '../plugins/media/Media2';
import { ListItemElement } from '../plugins/list/ListItemElement';
import { LinkElement } from '../plugins/link/LinkElement';
import { NOTE_HEADER, NOTE_HEADER_VOID } from '../plugins/layout/note-layout/createNoteLayoutPlugin';
import { NoteHeaderElement } from '../plugins/layout/note-layout/NoteHeaderElement';
import { MergeFieldElement } from '../plugins/merge-fields/MergeFieldElement';
import { SmartInputElement } from '../plugins/merge-fields/SmartInputElement';

const baseTextStyle = {
  fontFamily: 'Arial, Helvetica, sans-serif',
  lineHeight: 1.2,
  margin: 0,
};

// Custom withProps function so that the plate ui can combine inline styles,
// rather than overwrite.
const withProps = (Component, props) => {
  return (_props) => {
    const { style: style1 = {}, ...rest1 } = _props;
    const { style: style2 = {}, ...rest2 } = props;
    return (
      <Component
        {...rest1}
        {...rest2}
        style={{ ...style1, ...style2 }}
      />
    );
  };
};

export const plateUI = (overrideByKey, { placeholder } = {}) => {
  let components = {
    [MARK_BOLD]: withProps(PlateLeaf, { as: 'strong' }),
    [MARK_ITALIC]: withProps(PlateLeaf, { as: 'em' }),
    [MARK_UNDERLINE]: withProps(PlateLeaf, { as: 'u' }),
    [ELEMENT_H1]: withProps(HeadingElement, { as: 'h1', style: baseTextStyle }),
    [ELEMENT_H2]: withProps(HeadingElement, { as: 'h2', style: baseTextStyle }),
    [ELEMENT_H3]: withProps(HeadingElement, { as: 'h3', style: baseTextStyle }),
    [ELEMENT_H4]: withProps(HeadingElement, { as: 'h4', style: baseTextStyle }),
    [ELEMENT_H5]: withProps(HeadingElement, { as: 'h5', style: baseTextStyle }),
    [ELEMENT_H6]: withProps(HeadingElement, { as: 'h6', style: baseTextStyle }),
    [ELEMENT_PARAGRAPH]: withProps(
      ParagraphElement,
      {
        as: 'p',
        style: { ...baseTextStyle, fontSize: '13px', minHeight: '13px' },
      },
    ),
    [ELEMENT_LINK]: withProps(LinkElement, { style: { color: '#bea06e', textDecoration: 'underline' } }),
    [ELEMENT_OL]: withProps(ListElement, { as: 'ol', style: { paddingLeft: '10px', margin: '0' } }),
    [ELEMENT_UL]: withProps(ListElement, { as: 'ul', style: { paddingLeft: '10px', margin: '0' } }),
    [ELEMENT_LI]: ListItemElement,
    [ELEMENT_TODO_LI]: TodoListElement,
    [ELEMENT_IMAGE]: ImageElement2,
    [ELEMENT_MEDIA_EMBED]: withProps(MediaEmbedElement2, {
      caption: { disabled: true },
    }),
    [ELEMENT_TABLE]: TableElement,
    [ELEMENT_TD]: TableCellElement,
    [ELEMENT_TH]: TableCellElement,
    [ELEMENT_PAGE_BREAK]: PageBreakElement,
    [NOTE_HEADER]: NoteHeaderElement,
    [NOTE_HEADER_VOID]: NoteHeaderElement,
    [ELEMENT_MERGE_FIELD]: MergeFieldElement,
    [ELEMENT_SMART_INPUT]: SmartInputElement,
  };
  if (overrideByKey) {
    Object.keys(overrideByKey).forEach((key) => {
      components[key] = overrideByKey[key];
    });
  }
  if (placeholder) {
    components = withPlaceholders(components);
  }

  return components;
};

const components = plateUI;

export const plugins = createPlugins(
  [
    createTrailingBlockPlugin({ level: 0, type: ELEMENT_PARAGRAPH }),
    createErrorHandlingPlugin(),
    createResetNodePlugin(resetNodePlugin),
    createParagraphPlugin(),
    createAlignPlugin({
      inject: {
        props: {
          validTypes: VALID_ALIGNMENT_TYPES,
        },
      },
    }),
    createListPlugin(
      listPlugin,
      {
        ...listPluginByKey,
        [ELEMENT_OL]: {
          ...listPluginByKey[ELEMENT_OL],
          serializeHtml: withProps(ListElement, { as: 'ol', style: { paddingLeft: '20px', margin: '0' } }),
        },
        [ELEMENT_UL]: {
          ...listPluginByKey[ELEMENT_UL],
          serializeHtml: withProps(ListElement, { as: 'ul', style: { paddingLeft: '20px', margin: '0' } }),
        },
        [ELEMENT_LI]: {
          ...listPluginByKey[ELEMENT_LI],
          serializeHtml: withProps(ListItemElement, { style: baseTextStyle }),
        },
      },
    ),
    createLinkPlugin({
      ...linkPlugin,
      serializeHtml: withProps(LinkElement, { style: { color: '#bea06e', textDecoration: 'underline' } }),
      renderAfterEditable: LinkFloatingToolbar,
    }),

    createBoldPlugin(),
    createItalicPlugin(),
    createUnderlinePlugin(),

    createMergeFieldsPlugin(),

    createExitBreakPlugin(),

    createNodeIdPlugin(),
    createFakeSelectionPlugin(),
  ],
  { components: components() },
);

export const serializedPlugins = [
  createParagraphPlugin(),
  createAlignPlugin({
    inject: {
      props: {
        validTypes: VALID_ALIGNMENT_TYPES,
      },
    },
  }),
  createListPlugin(listPlugin, listPluginByKey),
  createLinkPlugin({ ...linkPlugin, renderAfterEditable: LinkFloatingToolbar }),

  createBoldPlugin({ serializeHtml: ({ children, leaf }) =>
    leaf[MARK_BOLD] && !!leaf.text ? <strong>{children}</strong> : children,
  }),
  createItalicPlugin({ serializeHtml: ({ children, leaf }) =>
    leaf[MARK_ITALIC] && !!leaf.text ? <em>{children}</em> : children,
  }),
  createUnderlinePlugin({ serializeHtml: ({ children, leaf }) =>
    leaf[MARK_UNDERLINE] && !!leaf.text ? <u>{children}</u> : children,
  }),

  createExitBreakPlugin(),

  { components: plateUI() },
];
